import { arraySort, transformFields } from '../../core/utils/methods';
import { createReducer, on } from '../../core/utils/store';
import { SITE_INFO_TRANSFORM_FIELDS } from '../../models/constants/transform-fields';
import {
  AssociatedProject,
  ContactsData,
  CreateSiteResp,
  OptionProps,
  PaginatedResponse,
  PayloadAndState,
  SiteAssociatedProjectsTabData,
  SiteBrowse,
  SiteContactsTabData,
  SiteInformationTabData,
} from '../../models/interfaces';
import { SiteActions } from '../actions/site.actions';

export interface SiteState {
  associatedProjects: AssociatedProject[];
  siteContacts: ContactsData[];
  siteBrowse: PaginatedResponse<SiteBrowse>;
  PSLocationCodeOptions: OptionProps[];
  MDGLocationCodeOptions: OptionProps[];
  currentSite: SiteBrowse;
  createdSiteUniqueFields: CreateSiteResp | null;
  activeOrAuthorizedProjectsCount: number;
  hasActiveOrAuthorizedProjects: boolean;
  loadingTabs: Record<string, boolean>;
  loadedTabs: Record<string, boolean>;
  tabErrors: Record<string, boolean>;
  isFetching: boolean;
  errors: boolean;
}

const initialState: SiteState = {
  associatedProjects: [],
  siteContacts: [],
  siteBrowse: {
    items: [],
    total: 0,
  },
  currentSite: {} as SiteBrowse,
  createdSiteUniqueFields: null,
  activeOrAuthorizedProjectsCount: 0,
  hasActiveOrAuthorizedProjects: false,
  loadingTabs: {},
  loadedTabs: {},
  tabErrors: {},
  PSLocationCodeOptions: [],
  MDGLocationCodeOptions: [],
  isFetching: false,
  errors: false,
};

const getAssociatedProjectsWithFuzeIDs = (
  associatedProjects: AssociatedProject[] = [],
): AssociatedProject[] =>
  associatedProjects.map((project: AssociatedProject) => ({
    ...project,
    fuzeIDs: project.fuzeIDs ?? project.categories ?? [],
    projectPaceTaskData: project.projectPaceTaskData ?? [],
  }));

export const reducer = createReducer(
  initialState,
  on(
    SiteActions.getSiteBrowseDataAction,
    ({
      payload: siteBrowse,
    }: PayloadAndState<PaginatedResponse<SiteBrowse>, SiteState>) => ({
      siteBrowse,
    }),
  ),
  on(
    SiteActions.getAssociatedProjectsDataAction,
    ({
      payload: associatedProjects,
    }: PayloadAndState<AssociatedProject[], SiteState>) => ({
      associatedProjects,
    }),
  ),
  on(
    SiteActions.setTabLoading,
    ({
      payload,
      state,
    }: PayloadAndState<
      { tabName: string; isLoading: boolean },
      SiteState
    >) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: payload.isLoading,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: false,
      },
    }),
  ),
  on(
    SiteActions.setTabLoaded,
    ({
      payload,
      state,
    }: PayloadAndState<{ tabName: string; isLoaded: boolean }, SiteState>) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: false,
      },
      loadedTabs: {
        ...state.loadedTabs,
        [payload.tabName]: payload.isLoaded,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: false,
      },
    }),
  ),
  on(
    SiteActions.setTabError,
    ({
      payload,
      state,
    }: PayloadAndState<{ tabName: string; hasError: boolean }, SiteState>) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: false,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: payload.hasError,
      },
    }),
  ),
  on(
    SiteActions.resetTabsData,
    ({ state }: PayloadAndState<unknown, SiteState>) => ({
      ...state,
      currentSite: {} as SiteBrowse,
      siteContacts: [],
      associatedProjects: [],
      activeOrAuthorizedProjectsCount: 0,
      hasActiveOrAuthorizedProjects: false,
      loadingTabs: {},
      loadedTabs: {},
      tabErrors: {},
    }),
  ),
  on(
    SiteActions.getSiteInformationAction,
    ({
      payload: {
        activeOrAuthorizedProjectsCount = 0,
        hasActiveOrAuthorizedProjects: hasActiveOrAuthorizedProjectsPayload,
        ...site
      },
      state,
    }: PayloadAndState<SiteInformationTabData, SiteState>) => ({
      ...state,
      currentSite: transformFields<SiteBrowse>(
        site,
        SITE_INFO_TRANSFORM_FIELDS,
      ),
      activeOrAuthorizedProjectsCount,
      hasActiveOrAuthorizedProjects:
        hasActiveOrAuthorizedProjectsPayload ??
        !!activeOrAuthorizedProjectsCount,
      loadingTabs: { ...state.loadingTabs, information: false },
      loadedTabs: { ...state.loadedTabs, information: true },
      tabErrors: { ...state.tabErrors, information: false },
    }),
  ),
  on(
    SiteActions.getSiteContactsAction,
    ({ payload, state }: PayloadAndState<SiteContactsTabData, SiteState>) => ({
      ...state,
      siteContacts: payload.contacts ?? [],
      loadingTabs: { ...state.loadingTabs, contacts: false },
      loadedTabs: { ...state.loadedTabs, contacts: true },
      tabErrors: { ...state.tabErrors, contacts: false },
    }),
  ),
  on(
    SiteActions.getSiteAssociatedProjectsAction,
    ({
      payload: { projects, ...site },
      state,
    }: PayloadAndState<SiteAssociatedProjectsTabData, SiteState>) => ({
      ...state,
      currentSite: {
        ...state.currentSite,
        ...site,
      },
      associatedProjects: getAssociatedProjectsWithFuzeIDs(projects),
      loadingTabs: { ...state.loadingTabs, associatedProjects: false },
      loadedTabs: { ...state.loadedTabs, associatedProjects: true },
      tabErrors: { ...state.tabErrors, associatedProjects: false },
    }),
  ),
  on(
    SiteActions.createSiteContactAction,
    ({
      payload,
      state: { siteContacts },
    }: PayloadAndState<ContactsData, SiteState>) => ({
      siteContacts: [...siteContacts, payload],
    }),
  ),
  on(
    SiteActions.createSiteAction,
    ({
      payload: createdSiteUniqueFields,
    }: PayloadAndState<CreateSiteResp, SiteState>) => ({
      createdSiteUniqueFields,
    }),
  ),
  on(
    SiteActions.updateSiteAction,
    ({
      payload,
      state: { currentSite },
    }: PayloadAndState<SiteBrowse, SiteState>) => ({
      currentSite: {
        ...currentSite,
        ...payload,
      },
    }),
  ),
  on(SiteActions.clearCreatedSite, (_: PayloadAndState<void, SiteState>) => ({
    createdSiteUniqueFields: null,
  })),
  on(
    SiteActions.updateSiteContactAction,
    ({
      payload,
      state: { siteContacts },
    }: PayloadAndState<ContactsData, SiteState>) => ({
      siteContacts: arraySort(
        [
          ...siteContacts.filter(
            (contact: ContactsData) => contact.id !== payload.id,
          ),
          payload,
        ],
        'ASC',
        'contactName',
      ),
    }),
  ),
  on(
    SiteActions.deleteSiteContactAction,
    ({
      payload: id,
      state: { siteContacts },
    }: PayloadAndState<string, SiteState>) => ({
      siteContacts: siteContacts.filter(
        (contact: ContactsData) => contact.id !== id,
      ),
    }),
  ),
  on(
    SiteActions.getPSLocationCodeOptionsList,
    ({ payload }: PayloadAndState<OptionProps[], SiteState>) => ({
      PSLocationCodeOptions: payload ?? [],
    }),
  ),
  on(
    SiteActions.getMDGLocationCodeOptionsList,
    ({ payload }: PayloadAndState<OptionProps[], SiteState>) => ({
      MDGLocationCodeOptions: payload ?? [],
    }),
  ),
);
